import { Alert, Button, Drawer, Group, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formOfItem,
  formOfJob,
  ItemFields,
  itemBodyOf,
  JobFields,
  jobBodyOf,
  useLibraryErrorText,
} from './LibraryTables.js';
import type { PmsLibrarySource } from './source.js';
import type { CreatedEntry, PmsLibrary } from './types.js';
import { suggestedItemCode, suggestedJobCode, suggestedSystemCode } from './view.js';

/** Ne eklenecek: şirketin sistemi, sisteme kalem, kaleme iş (ADR 0102). */
export type AddTarget =
  | { kind: 'system' }
  | { kind: 'item'; systemId: string | null }
  | { kind: 'job'; itemId: string | null };

/**
 * Elle ekleme çekmecesi (ADR 0102): alanlar düzenleme çekmeceleriyle aynı; kod sıradaki numarayla
 * önerilir ve değiştirilebilir. Tekillik ve sınırlar sunucuda denetlenir (`DUPLICATE_LIBRARY_CODE`,
 * `LIBRARY_INTERVAL_REQUIRED`).
 */
export function AddEntryDrawer({
  target,
  library,
  source,
  onClose,
  onAdded,
}: {
  target: AddTarget | null;
  library: PmsLibrary;
  source: PmsLibrarySource;
  onClose: () => void;
  onAdded: (kind: AddTarget['kind'], entry: CreatedEntry) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useLibraryErrorText();
  const [parentId, setParentId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [system, setSystem] = useState({ name: '', remarks: '' });
  const [item, setItem] = useState(() => formOfItem(null));
  const [job, setJob] = useState(() => formOfJob(null));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Çekmece her açılışta temiz başlar; üst kayıt ve kod önerisi hedeften
  useEffect(() => {
    if (target === null) return;
    const parent =
      target.kind === 'item' ? target.systemId : target.kind === 'job' ? target.itemId : null;
    setParentId(parent);
    setCodeTouched(false);
    setSystem({ name: '', remarks: '' });
    setItem(formOfItem(null));
    setJob(formOfJob(null));
    setError(null);
  }, [target]);

  // Kullanıcı kodu değiştirmediyse öneri üst kayıtla birlikte değişir
  useEffect(() => {
    if (target === null || codeTouched) return;
    setCode(
      target.kind === 'system'
        ? suggestedSystemCode(library)
        : target.kind === 'item'
          ? suggestedItemCode(library, parentId)
          : suggestedJobCode(library, parentId),
    );
  }, [target, parentId, codeTouched, library]);

  const kind = target?.kind ?? 'job';
  const k = (key: string) => t(`pmsLibrary.add.${kind}.${key}`);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const entry =
        kind === 'system'
          ? await source.createSystem({
              code,
              name: system.name,
              remarks: system.remarks.trim() === '' ? null : system.remarks,
            })
          : kind === 'item'
            ? await source.createItem({ systemId: parentId, code, ...itemBodyOf(item, null) })
            : await source.createJob({ itemId: parentId, code, ...jobBodyOf(job, null) });
      await onAdded(kind, entry);
      onClose();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  const missingParent = kind !== 'system' && parentId === null;

  return (
    <Drawer
      opened={target !== null}
      onClose={onClose}
      position="right"
      size="md"
      title={target ? k('title') : ''}
      closeButtonProps={{ 'aria-label': t('pmsLibrary.actions.close') }}
    >
      {target ? (
        <Stack gap="sm">
          {kind === 'item' ? (
            <Select
              label={k('parent')}
              placeholder={k('parentPlaceholder')}
              required
              searchable
              allowDeselect={false}
              data={library.systems.map((s) => ({ value: s.id, label: `${s.code} ${s.name}` }))}
              value={parentId}
              onChange={setParentId}
            />
          ) : null}
          {kind === 'job' ? (
            <Select
              label={k('parent')}
              placeholder={k('parentPlaceholder')}
              required
              searchable
              allowDeselect={false}
              limit={50}
              data={library.items.map((i) => ({
                value: i.id,
                label: `${i.code} ${i.subSystem ?? i.name}`,
              }))}
              value={parentId}
              onChange={setParentId}
            />
          ) : null}
          <TextInput
            label={k('code')}
            description={k('codeHint')}
            required
            value={code}
            onChange={(event) => {
              setCode(event.currentTarget.value);
              setCodeTouched(true);
            }}
          />
          {kind === 'system' ? (
            <>
              <TextInput
                label={k('name')}
                required
                value={system.name}
                onChange={(event) => {
                  const name = event.currentTarget.value;
                  setSystem((v) => ({ ...v, name }));
                }}
              />
              <Textarea
                label={k('remarks')}
                autosize
                minRows={2}
                value={system.remarks}
                onChange={(event) => {
                  const remarks = event.currentTarget.value;
                  setSystem((v) => ({ ...v, remarks }));
                }}
              />
            </>
          ) : null}
          {kind === 'item' ? (
            <ItemFields values={item} onChange={(patch) => setItem((v) => ({ ...v, ...patch }))} />
          ) : null}
          {kind === 'job' ? (
            <JobFields values={job} onChange={(patch) => setJob((v) => ({ ...v, ...patch }))} />
          ) : null}
          {kind === 'job' ? (
            <Alert color="blue" variant="light">
              {t('pmsLibrary.add.job.vesselHint')}
            </Alert>
          ) : null}
          {error ? <Alert color="red">{error}</Alert> : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('pmsLibrary.actions.cancel')}
            </Button>
            <Button loading={busy} disabled={missingParent} onClick={() => void save()}>
              {t('pmsLibrary.actions.add')}
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Drawer>
  );
}
